import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import rootStore from './store/store';

import {
  storeMaxAge,
  userActivityTrackingDebounceTime,
  storeExpiryEnabled,
  sessionTimeoutPurgeInclusionRoutes,
  appMaxInactiveTimeToReloadInMNS,
} from './constants/globals';

import { activateUserActivityTracker } from './utils/helpers';
import { syncSupportedCurrenciesFromState } from './utils/currencyDisplay';
import {
  persistLastActive,
  activateInactivityReset,
  getLastActiveAt,
} from './utils/activateInactivityReset';
import { markPersistorForPurgeAction } from './actions/app.action';

import App from './containers';

import './index.scss';

// Native WebView PTR reads WebView contentOffset (document scroll). FUDR-13758 scrolls inside
// `.container` on web; nested page shells (100vh + overflow-y:auto) also keep contentOffset.y
// at 0 → PTR stays armed. Keep document as the scroll root in-app (see index.scss).
if (typeof window !== 'undefined' && window.ReactNativeWebView) {
  document.documentElement.classList.add('native-webview');
}

// Defer non-critical polyfill loading
import('smoothscroll-polyfill').then(smoothscroll => {
  // inject the smooth scroll polyfill
  smoothscroll.polyfill();
});

const { store, persistor } = rootStore;

store.subscribe(syncSupportedCurrenciesFromState);
syncSupportedCurrenciesFromState();

const inactivityResetEnabled = appMaxInactiveTimeToReloadInMNS > 0;

// Session state reset flow + FUDR-13118 last-active persistence (shared sessionStorage key)
if (!!storeExpiryEnabled || inactivityResetEnabled) {
  activateUserActivityTracker(() => {
    const currentTime = Date.now();
    const userLastActiveTime = getLastActiveAt();

    const currentRoute = window.location.pathname.split('/')[1] || '';
    const isCurrentRouteSessionTimeoutScoped =
      sessionTimeoutPurgeInclusionRoutes.includes(currentRoute);

    if (
      !!storeExpiryEnabled &&
      !!userLastActiveTime &&
      currentTime - userLastActiveTime > storeMaxAge &&
      isCurrentRouteSessionTimeoutScoped
    ) {
      store.dispatch(markPersistorForPurgeAction(true));
    }
    persistLastActive(currentTime);
  }, userActivityTrackingDebounceTime);
}

activateInactivityReset({ store, persistor });

ReactDOM.render(
  <Provider store={store}>
    <App />
  </Provider>,
  document.getElementById('root'),
);
